import { useCallback, useEffect, useState } from 'react';
import {
  getExecutionLogPage, getKrSimulationDecisionPage, getRealizedTradePage,
  KrSimulationDecision, RealizedTrade,
} from '../services/historyApi';
import type { PageResponse } from '../types/pagination';
import type { ExecutionLog } from '../types/strategy';
import type { HistoryTab } from '../pages/TradingHistory';
import type { MarketScope } from '../types/marketScope';

const PAGE_SIZE = 25;
const emptyExecutionPage: PageResponse<ExecutionLog> = {
  items: [], total: 0, limit: PAGE_SIZE, offset: 0, has_more: false,
};
const emptyTradePage: PageResponse<RealizedTrade> = {
  items: [], total: 0, limit: PAGE_SIZE, offset: 0, has_more: false,
};
const emptyKrPage: PageResponse<KrSimulationDecision> = {
  items: [], total: 0, limit: PAGE_SIZE, offset: 0, has_more: false,
};

export function useTradingHistory(usRealizedScope: 'paper' | 'live' = 'live') {
  const [tab, setTab] = useState<HistoryTab>('execution');
  const [market, setMarket] = useState<MarketScope>('US');
  const [executionPage, setExecutionPage] = useState(emptyExecutionPage);
  const [tradePage, setTradePage] = useState(emptyTradePage);
  const [krDecisionPage, setKrDecisionPage] = useState(emptyKrPage);
  const [offset, setOffset] = useState(0);
  const [symbol, setSymbol] = useState('');
  const [action, setAction] = useState('ALL');
  const [tradeType, setTradeType] = useState('ALL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = { limit: PAGE_SIZE, offset, startDate, endDate };
      if (market === 'KR' && tab === 'execution') {
        setKrDecisionPage(await getKrSimulationDecisionPage({ ...query, symbol, action }));
      } else if (market === 'KR') {
        setTradePage(emptyTradePage);
      } else if (tab === 'execution') {
        setExecutionPage(await getExecutionLogPage({ ...query, symbol, action, market }));
      } else {
        const realizedMarket = market === 'ALL' ? 'US' : market;
        setTradePage(await getRealizedTradePage({
          ...query, tradeType, market: realizedMarket,
          scope: realizedMarket === 'US' ? usRealizedScope : undefined,
        }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '이력 조회 실패');
    } finally {
      setLoading(false);
    }
  }, [action, endDate, market, offset, startDate, symbol, tab, tradeType, usRealizedScope]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const changeTab = (nextTab: HistoryTab) => {
    setTab(nextTab);
    setOffset(0);
  };

  const changeMarket = (nextMarket: MarketScope) => {
    setMarket(nextMarket);
    setOffset(0);
  };

  return {
    tab, market, executionPage, tradePage, krDecisionPage, offset, symbol, action, tradeType,
    startDate, endDate, loading, error, setOffset, setSymbol, setAction,
    setTradeType, setStartDate, setEndDate, changeTab, changeMarket,
  };
}
